import type { Account, AccountProfile, Plan, WorkspaceMember } from './types';

/**
 * ILLUSTRATIVE FIXTURES — the account, company and plan data the `/account` page shows until
 * WorkOS profile fields and billing are wired. People and company are invented; where the signed-in
 * user's real profile (`api.users.me`) has a field, `mergeSignedInIdentity` lays it over these.
 */

/** @desc The workspace team. One admin, as the home page's crown badge needs. */
export const WORKSPACE_MEMBERS: readonly WorkspaceMember[] = [
  {
    id: 'member-1',
    firstName: 'Amelia',
    lastName: 'Hart',
    email: 'amelia.hart@northgate-capital.example',
    jobTitle: 'Head of Research',
    role: 'admin',
    lastActive: '2026-10-13',
  },
  {
    id: 'member-2',
    firstName: 'Daniel',
    lastName: 'Osei',
    email: 'daniel.osei@northgate-capital.example',
    jobTitle: 'Equity Analyst',
    role: 'member',
    lastActive: '2026-10-12',
  },
  {
    id: 'member-3',
    firstName: 'Priya',
    lastName: 'Raman',
    email: 'priya.raman@northgate-capital.example',
    jobTitle: 'Associate',
    role: 'member',
    lastActive: '2026-10-09',
  },
  {
    id: 'member-4',
    firstName: 'Tomás',
    lastName: 'Ferreira',
    email: 'tomas.ferreira@northgate-capital.example',
    jobTitle: 'Portfolio Manager',
    role: 'member',
    lastActive: '2026-10-02',
  },
];

export const ACCOUNT: Account = {
  profile: {
    firstName: 'Amelia',
    lastName: 'Hart',
    email: 'amelia.hart@northgate-capital.example',
    jobTitle: 'Head of Research',
    timeZone: 'Europe/London',
  },
  company: {
    name: 'Northgate Capital',
    website: 'https://northgate-capital.example',
    sector: 'Investment management',
    size: '51–200',
    address: ['12 Finsbury Square', 'London', 'EC2A 1AS', 'United Kingdom'],
    members: [...WORKSPACE_MEMBERS],
  },
  currentPlanId: 'plus',
  options: {
    timeZones: [
      'Europe/London',
      'Europe/Dublin',
      'Europe/Paris',
      'Europe/Madrid',
      'Europe/Stockholm',
      'America/New_York',
      'America/Toronto',
      'America/Sao_Paulo',
      'Australia/Sydney',
      'Africa/Johannesburg',
    ],
    sectors: [
      'Investment management',
      'Investment banking',
      'Gambling operator',
      'Gambling supplier',
      'Regulator or government',
      'Consultancy',
      'Media',
      'Other',
    ],
    sizes: ['1–10', '11–50', '51–200', '201–1,000', '1,000+'],
  },
};

/**
 * @desc The fields of the signed-in user (`api.users.me`) the fixtures defer to. Structural, so the
 * data layer does not depend on the tRPC router's types; `users.me`'s result satisfies it.
 */
export type SignedInUser = {
  _id: string;
  email: string;
  name: { first: string; last: string };
  /** @desc The WorkOS organisation's name; `null` for an org-less invitation. */
  organisationName?: string | null;
};

/** @desc A member's display name: first and last, else the email when WorkOS has no name. */
export const memberDisplayName = (
  member: Pick<WorkspaceMember, 'firstName' | 'lastName' | 'email'>,
) => [member.firstName, member.lastName].filter(Boolean).join(' ') || member.email;

/**
 * @desc The workspace team with the signed-in user in it: first, and its admin (Figma `426:7806`
 * lists the viewer first with the crown). They take the slot of the fixture persona — the member
 * whose email is the fixture profile's, who stood for "you" — so the team neither shows a ghost of
 * the invented persona nor two people with the persona's job title; every other fixture member
 * follows, as a member. Name, email and id are real; job title and last active stay the persona's
 * (illustrative). WorkOS carries no workspace role, so "admin" is the demo's assumption, not a
 * permission — the admin-panel link keeps following the real `isAdmin` check. Without a signed-in
 * user the fixture team is returned unchanged.
 */
export const mergeWorkspaceMembers = (
  members: readonly WorkspaceMember[],
  me: SignedInUser | null | undefined,
  persona: AccountProfile,
): WorkspaceMember[] => {
  if (!me) return [...members];
  const personaMember = members.find((member) => member.email === persona.email);
  const self: WorkspaceMember = {
    id: me._id,
    firstName: me.name.first,
    lastName: me.name.last,
    email: me.email,
    jobTitle: personaMember?.jobTitle ?? persona.jobTitle,
    role: 'admin',
    // Signed in now, so no less recent than anyone else in the team.
    lastActive:
      personaMember?.lastActive ??
      members.reduce(
        (latest, member) => (member.lastActive > latest ? member.lastActive : latest),
        '',
      ),
  };
  const others = members
    .filter((member) => member !== personaMember && member.email !== me.email)
    .map((member): WorkspaceMember => ({ ...member, role: 'member' }));
  return [self, ...others];
};

/**
 * @desc The account as the signed-in user should see it (gaps A6, H11): their real name and email
 * on the profile, their WorkOS organisation's name on the company, and themselves at the head of
 * the team. Fields the real record lacks — job title, time zone, the rest of the company, the
 * other members, the plan — stay the illustrative fixture's. Real names win even when empty, so a
 * missing last name never borrows the persona's. Without a signed-in user (still loading, or the
 * query failed) the fixture is returned unchanged.
 */
export const mergeSignedInIdentity = (
  account: Account,
  me: SignedInUser | null | undefined,
): Account => {
  if (!me) return account;
  return {
    ...account,
    profile: {
      ...account.profile,
      firstName: me.name.first,
      lastName: me.name.last,
      email: me.email,
    },
    company: {
      ...account.company,
      name: me.organisationName || account.company.name,
      members: mergeWorkspaceMembers(account.company.members, me, account.profile),
    },
  };
};

/**
 * @desc The three tiers as the Figma plan cards draw them (`295:54048`, decision D-14): prices,
 * seats and feature lists are Figma's own content and are placeholders pending tiering — the
 * workbook carries that decision, and the page says so behind the Illustrative badge.
 */
export const PLANS: readonly Plan[] = [
  {
    id: 'free',
    name: 'H2GC Free',
    price: '£0',
    period: 'per month',
    seats: '1 seat',
    tagline: 'Stay across the industry.',
    cta: 'Switch to H2GC Free',
    features: [
      'Market-sizing tools',
      'Analysis datasets',
      'H2Agent Answer',
      'Press reports and monitoring',
    ],
  },
  {
    id: 'plus',
    name: 'H2GC Plus',
    price: '£99',
    period: 'per month',
    seats: 'Up to 5 seats',
    tagline: 'Company and market research.',
    cta: 'Upgrade to H2GC Plus',
    includes: 'free',
    features: [
      'Deeper market-sizing tooling',
      'Regional analytics reports',
      'Larger analysis datasets',
      'Forecasting',
      'Channel segregation',
      'H2Agent Analyst',
    ],
  },
  {
    id: 'pro',
    name: 'H2GC Pro',
    price: '£499',
    period: 'per month',
    seats: 'Up to 25 seats',
    tagline: 'Data and reports at scale.',
    cta: 'Use H2GC like a Pro',
    includes: 'plus',
    features: [
      'Operator and company data',
      'Custom reports',
      'Player surveys',
      'Prediction markets',
      'Affiliates and payments',
    ],
  },
];
